import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Home } from '@lmc/ts-common'
import { NAMESPACE } from 'locale'
import styled from 'styled-components'
import { Tag } from 'antd'
import {
  PageHeader,
  Spinner,
  Button,
  FilterContainer,
  MultiSelect
} from '@lmc/ui-kit'
import { ListItem } from '@lmc/ui-kit/src/dataDisplay'
import { Pagination } from '@lmc/ui-kit/src/navigation/Pagination'
import { Text } from '@lmc/ui-kit/src/general'
import { PlusOutlined } from '@ant-design/icons'
import PageWrapper from '../componentsV2/PageWrapper'
import { getMedicationIcon } from '../utils/medicationUtils'
import useResident from 'hooks/useResident'
import Operations from '../../../../../operations'
import Actions from '../../../../../actions'
import { useMedicationSchedulePatterns } from '../hooks/useMedicationSchedulePatterns'

export const MedicationListScreen = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { id: residentId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const { resident, isLoading: isLoadingResident } = useResident({
    id: residentId
  })

  const user = useSelector(
    (state: { global: { user?: { medicationPermission?: boolean } } }) =>
      state.global.user || { medicationPermission: true }
  )
  const [selectedMedicationTypes, setSelectedMedicationTypes] = useState<
    string[]
  >([])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['active'])
  const [currentPage, setCurrentPage] = useState(1)

  const { data: medicationSchedulePatterns, isLoading: isLoadingPatterns } =
    useMedicationSchedulePatterns({
      residentId: residentId || '',
      includeStock: 'true',
      enabled: !!residentId
    })

  const filteredMedications = useMemo(() => {
    if (!medicationSchedulePatterns) return []

    let filtered = [...medicationSchedulePatterns]

    if (selectedMedicationTypes.length > 0) {
      filtered = filtered.filter((med) => {
        if (selectedMedicationTypes.includes('scheduled') && !med.prn) {
          return true
        }
        if (selectedMedicationTypes.includes('prn') && med.prn) {
          return true
        }
        return false
      })
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((med) => {
        const status = med.status?.toLowerCase()
        if (selectedStatuses.includes('active') && status === 'active') {
          return true
        }
        if (
          selectedStatuses.includes('discontinued') &&
          status === 'discontinued'
        ) {
          return true
        }
        return false
      })
    }

    return filtered.sort((a, b) => {
      const nameA = (a.medicationName || '').toLowerCase()
      const nameB = (b.medicationName || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [medicationSchedulePatterns, selectedMedicationTypes, selectedStatuses])

  const paginatedMedications = useMemo(() => {
    const startIndex = (currentPage - 1) * 10
    const endIndex = startIndex + 10
    return filteredMedications.slice(startIndex, endIndex)
  }, [filteredMedications, currentPage])

  useEffect(() => {
    if (currentPage > 1 && paginatedMedications.length === 0) {
      setCurrentPage(1)
    }
  }, [paginatedMedications.length, currentPage])

  const isInitialAssessment =
    resident?.status === 'pre-assessment' || resident?.isActiveInitialAssessment

  const handleAddMedication = () => {
    if (!resident) return
    dispatch(Operations.openModal({ modalId: 'addEmarMedication' }))
    dispatch(
      Actions.addForm({
        formId: 'addEmarMedication',
        initial: { resident: resident.id }
      })
    )
  }

  const handleMedicationTypeChange = (types: string[]) => {
    setSelectedMedicationTypes(types)
  }

  const handleStatusChange = (statuses: string[]) => {
    setSelectedStatuses(statuses)
  }

  const medicationTypeOptions = [
    { label: t('SCHEDULED'), value: 'scheduled' },
    { label: t('PRN'), value: 'prn' }
  ]

  const statusOptions = [
    { label: t('ACTIVE'), value: 'active' },
    { label: t('DISCONTINUED'), value: 'discontinued' }
  ]

  if (isLoadingResident || !resident) {
    return (
      <PageWrapper>
        <Spinner style={{ marginTop: '80px' }} />
      </PageWrapper>
    )
  }

  return (
    <>
      <PageHeader title={t('MEDICATION_LIST')} sticky>
        {user.medicationPermission && !isInitialAssessment && (
          <Button
            id="add-medication-button"
            onClick={handleAddMedication}
            variant="primary"
            disabled={home && !home.features?.emarOnboarded?.enabled}
          >
            <PlusOutlined />
            {t('ADD_MEDICATION')}
          </Button>
        )}
      </PageHeader>
      <PageWrapper>
        <FilterContainer title="">
          <>
            <StyledMedicationTypeSelect
              title={t('MEDICATION_TYPE')}
              options={medicationTypeOptions}
              values={selectedMedicationTypes}
              onChange={handleMedicationTypeChange}
              hasClear
              hasSearch
              hasSelectAll
              data-testid="medication-type-filter"
            />
            <StyledStatusSelect
              title={t('STATUS')}
              options={statusOptions}
              values={selectedStatuses}
              onChange={handleStatusChange}
              hasClear
              hasSearch
              hasSelectAll
              data-testid="medication-status-filter"
            />
          </>
        </FilterContainer>
        {isLoadingPatterns ? (
          <Spinner />
        ) : filteredMedications.length === 0 ? (
          <EmptyStateContainer>
            <Text>{t('NO_MEDICATIONS_FOUND')}</Text>
          </EmptyStateContainer>
        ) : (
          <>
            <MedicationListContainer>
              {paginatedMedications.map((medication) => {
                const status = medication.status?.toLowerCase()
                const isDiscontinued = status === 'discontinued'
                const isPaused = status === 'paused'

                const handleClick = () => {
                  navigate(`/residents/${residentId}/emar/${medication._id}`)
                }

                return (
                  <StyledListItem
                    key={medication._id}
                    icon={getMedicationIcon(medication.status)}
                    onClick={handleClick}
                    title={
                      <ClickableMedicationName>
                        {medication.medicationName}
                      </ClickableMedicationName>
                    }
                    rightChildren={
                      <TagsContainer>
                        {isDiscontinued && (
                          <StyledTag color="default">
                            {t('DISCONTINUED')}
                          </StyledTag>
                        )}
                        {medication.controlledDrug && (
                          <StyledTag color="red">{t('CD')}</StyledTag>
                        )}
                        {medication.prn && (
                          <StyledTag color="blue">{t('PRN')}</StyledTag>
                        )}
                      </TagsContainer>
                    }
                  />
                )
              })}
            </MedicationListContainer>
            <PaginationContainer>
              <Pagination
                current={currentPage}
                pageSize={10}
                total={filteredMedications.length}
                onChange={setCurrentPage}
                showSizeChanger={false}
              />
            </PaginationContainer>
          </>
        )}
      </PageWrapper>
    </>
  )
}

const StyledMedicationTypeSelect = styled(MultiSelect)`
  min-width: 168px;
` as typeof MultiSelect

const StyledStatusSelect = styled(MultiSelect)`
  min-width: 156px;
` as typeof MultiSelect

const MedicationListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ClickableMedicationName = styled(Text)`
  cursor: pointer;
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 700;
  &:hover {
    text-decoration: underline;
  }
`

const StyledListItem = styled(ListItem)`
  background-color: ${({ theme }) => theme.color.neutral.color11};
  padding: 12px 16px;

  > div:first-child > div:first-child {
    height: 24px;
  }
`

const StyledTag = styled(Tag)`
  margin: 0;
`

const TagsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 24px 0;
`

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  margin-top: 24px;
`

export default MedicationListScreen
